import React from 'react';

import {
  PLACE_SEARCH_TYPES,
  addSearchFilter,
  filterPlans,
  getVisibleGroupResults,
  getVisibleUserResults,
} from '@components/Explorer/search';
import type {
  ExplorerDisplayMode,
  PlaceSearchResult,
  SearchResultFilter,
  SearchTargets,
  SelectedSearchFilter,
} from '@components/Explorer/search';
import {
  usePlaceAutocompleteQuery,
  usePlaceAutocompleteSelectionMutation,
  usePlaceAutocompleteSession,
} from '@db/Places/Queries';
import { useGroupSearch, useUserSearch } from '@db/Search';
import { errorAlert } from '@helpers/Alert';
import { hasLoggedError, logError } from '@helpers/Loggers';
import { DateRange, Group, Plan, User } from '@types';

type UseExplorerSearchProps = {
  plans: Plan[];
  groups: Group[];
  users: User[];
  searchTargets: SearchTargets;
  searchGroupIds?: string[];
  searchUserIds?: string[];
};

export function useExplorerSearch({
  plans,
  groups,
  users,
  searchTargets,
  searchGroupIds,
  searchUserIds,
}: UseExplorerSearchProps) {
  const [searchText, setSearchText] = React.useState('');
  const [selectedFilters, setSelectedFilters] = React.useState<SelectedSearchFilter[]>([]);
  const [displayMode, setDisplayMode] = React.useState<ExplorerDisplayMode>();
  const [dateRange, setDateRange] = React.useState<DateRange>();
  const { loading: usersLoading, results: userResults } = useUserSearch({
    query: searchTargets.users ? searchText : '',
    includedUserIds: searchUserIds,
  });
  const { loading: groupsLoading, results: groupResults } = useGroupSearch({
    includedGroupIds: searchGroupIds,
    query: searchTargets.groups ? searchText : '',
  });
  const {
    loading: placesLoading,
    selectionLoading: placeSelectionLoading,
    resolveResult: resolvePlaceSearchResult,
    results: placeResults,
  } = usePlaceSearch({
    query: searchTargets.places ? searchText : '',
  });
  const filteredPlans = React.useMemo(
    () => filterPlans(plans, selectedFilters, dateRange, displayMode),
    [plans, selectedFilters, dateRange, displayMode]
  );
  const visibleUserResults = React.useMemo(
    () => (searchTargets.users ? getVisibleUserResults(userResults, users, searchUserIds) : []),
    [users, userResults, searchTargets.users, searchUserIds]
  );
  const visibleGroupResults = React.useMemo(
    () =>
      searchTargets.groups ? getVisibleGroupResults(groupResults, groups, searchGroupIds) : [],
    [groups, groupResults, searchTargets.groups, searchGroupIds]
  );

  const selectFilter = React.useCallback(
    async (filter: SearchResultFilter) => {
      if (filter.type === 'place') {
        const selectPlaceFilter = async () => {
          try {
            const selection = await resolvePlaceSearchResult(filter.place);
            if (!selection) return undefined;

            setSelectedFilters((currentFilters) =>
              addSearchFilter(currentFilters, {
                type: 'place',
                place: selection.place,
                viewport: selection.viewport,
              })
            );
            setSearchText('');
            return selection;
          } catch (error) {
            errorAlert(error, { onAction: () => selectPlaceFilter() });
            if (!hasLoggedError(error)) {
              logError(error);
            }
            return undefined;
          }
        };

        return selectPlaceFilter();
      }

      setSelectedFilters((currentFilters) => addSearchFilter(currentFilters, filter));
      setSearchText('');
      return undefined;
    },
    [resolvePlaceSearchResult]
  );

  return {
    searchText,
    selectedFilters,
    displayMode,
    dateRange,
    usersLoading,
    groupsLoading,
    placesLoading,
    placeSelectionLoading,
    placeResults,
    visibleUserResults,
    visibleGroupResults,
    filteredPlans,
    setSearchText,
    setSelectedFilters,
    setDisplayMode,
    setDateRange,
    selectFilter,
  };
}

function usePlaceSearch({ query }: { query: string }) {
  const { resetSessionToken, sessionToken } = usePlaceAutocompleteSession();
  const autocompleteQuery = usePlaceAutocompleteQuery({
    query,
    sessionToken,
    types: PLACE_SEARCH_TYPES,
  });
  const { loading: selectionLoading, resolveResult: resolvePlaceAutocompleteResult } =
    usePlaceAutocompleteSelectionMutation();

  const resolveResult = React.useCallback(
    async (result: PlaceSearchResult) => {
      const selection = await resolvePlaceAutocompleteResult(result, sessionToken);
      if (selection) resetSessionToken();

      return selection;
    },
    [resetSessionToken, resolvePlaceAutocompleteResult, sessionToken]
  );

  return {
    loading: autocompleteQuery.loading || selectionLoading,
    resolveResult,
    results: autocompleteQuery.results,
    selectionLoading,
  };
}
